import { useState, useEffect, useRef } from 'react';
import { Shield, Key, AlertTriangle, CheckCircle, Copy, RefreshCw } from 'lucide-react';
import { Button, Input, Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { TwoFactorSetupResponse, TwoFactorStatusResponse } from '@/types';

export function TwoFactorSettings() {
  const [status, setStatus] = useState<TwoFactorStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (isSetupMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSetupMode]);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/2fa/status');
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetup = async () => {
    setIsSubmitting(true);
    try {
      const response = await api.post('/2fa/setup');
      if (response.data.success) {
        setSetupData(response.data.data);
        setIsSetupMode(true);
        setVerificationCode('');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '启动双因素认证设置失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('请输入6位验证码');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/2fa/setup/confirm', { code: verificationCode });
      if (response.data.success) {
        toast.success('双因素认证已启用');
        setIsSetupMode(false);
        setSetupData(null);
        setVerificationCode('');
        fetchStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '验证码无效');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast.error('请输入密码');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/2fa/disable', { password: disablePassword });
      if (response.data.success) {
        toast.success('双因素认证已禁用');
        setShowDisableModal(false);
        setDisablePassword('');
        fetchStatus();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '禁用失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!confirm('生成新的备用码将使旧的备用码失效。确定要继续吗？')) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/2fa/backup-codes/regenerate');
      if (response.data.success) {
        setNewBackupCodes(response.data.data.backupCodes);
        setShowBackupCodes(true);
        toast.success('新的备用码已生成');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || '生成备用码失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('已复制到剪贴板');
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Shield className="h-5 w-5" />
          <CardTitle>双因素认证</CardTitle>
        </div>
        <CardDescription>
          为您的账户添加额外的安全保护
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status?.enabled && !isSetupMode ? (
          <>
            <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">已启用</span>
            </div>
            {status.enabledAt && (
              <p className="text-sm text-muted-foreground">
                启用时间: {new Date(status.enabledAt).toLocaleString('zh-CN')}
              </p>
            )}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={handleRegenerateBackupCodes}
                disabled={isSubmitting}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                重新生成备用码
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDisableModal(true)}
              >
                禁用
              </Button>
            </div>
          </>
        ) : isSetupMode && setupData ? (
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-4">
              <h4 className="font-medium">步骤 1: 扫描二维码</h4>
              <p className="text-sm text-muted-foreground">
                使用验证器应用（如 Google Authenticator、Microsoft Authenticator）扫描下方二维码或手动输入密钥
              </p>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-lg">
                  {/* QR Code would be generated here */}
                  <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupData.uri)}`}
                      alt="2FA QR Code"
                      className="w-full h-full"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">手动输入密钥:</p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 p-2 bg-background rounded text-sm font-mono">
                    {setupData.secret}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.secret)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-200">重要：保存备用码</h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    如果您丢失了验证器，可以使用以下备用码登录。每个备用码只能使用一次。
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {setupData.backupCodes.map((code, index) => (
                      <code
                        key={index}
                        className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded text-sm font-mono text-center"
                      >
                        {code}
                      </code>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(setupData.backupCodes.join('\n'))}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    复制所有备用码
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">步骤 2: 输入验证码</h4>
              <p className="text-sm text-muted-foreground">
                输入验证器应用中显示的6位数字验证码
              </p>
              <Input
                ref={inputRef}
                type="text"
                placeholder="000000"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest"
              />
            </div>

            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsSetupMode(false);
                  setSetupData(null);
                  setVerificationCode('');
                }}
              >
                取消
              </Button>
              <Button
                onClick={handleConfirmSetup}
                disabled={isSubmitting || verificationCode.length !== 6}
              >
                {isSubmitting ? '验证中...' : '确认启用'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-muted-foreground">
              双因素认证为您的账户增加了一层安全保护。登录时，除了密码外，您还需要输入验证器应用生成的验证码。
            </p>
            <Button onClick={handleSetup} disabled={isSubmitting}>
              <Key className="h-4 w-4 mr-2" />
              启用双因素认证
            </Button>
          </>
        )}
      </CardContent>

      {/* Disable Modal */}
      {showDisableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>禁用双因素认证</CardTitle>
              <CardDescription>
                请输入您的密码以确认禁用双因素认证
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="password"
                placeholder="输入密码"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDisableModal(false);
                    setDisablePassword('');
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDisable}
                  disabled={isSubmitting || !disablePassword}
                >
                  {isSubmitting ? '禁用中...' : '确认禁用'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Backup Codes Modal */}
      {showBackupCodes && newBackupCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>新的备用码</CardTitle>
              <CardDescription>
                请妥善保存这些备用码，它们只会显示一次
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {newBackupCodes.map((code, index) => (
                  <code
                    key={index}
                    className="p-2 bg-muted rounded text-sm font-mono text-center"
                  >
                    {code}
                  </code>
                ))}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(newBackupCodes.join('\n'))}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  复制所有备用码
                </Button>
                <Button onClick={() => setShowBackupCodes(false)}>
                  关闭
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}
